import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Heart, Loader2, LockKeyhole, Mail, ShieldCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";

export default function DonorSignin() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [mode, setMode] = useState("password");
  const [registered, setRegistered] = useState("");
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState("");
  const [magicSent, setMagicSent] = useState(null);
  const consumed = useRef(false);
  const token = params.get("token");

  const load = useCallback(async () => {
    const res = await api.get("/donor/portal-config");
    setCfg(res.data);
  }, []);

  useEffect(() => { load().catch(() => setCfg(false)); }, [load]);

  useEffect(() => {
    if (!token || consumed.current) return;
    consumed.current = true;
    (async () => {
      try {
        await api.post(`/donor/magic-link/${token}`);
        navigate("/donor", { replace: true });
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, [token, navigate]);

  const signIn = async () => {
    setBusy("in");
    try {
      await api.post("/donor/login", form);
      navigate("/donor", { replace: true });
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const register = async () => {
    setBusy("reg");
    try {
      const res = await api.post("/donor/register", {
        email: form.email, password: form.password, name: form.name || "",
      });
      if (res.data.active) {
        toast.success("Account created — welcome");
        navigate("/donor", { replace: true });
        return;
      }
      setRegistered(res.data.message);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : apiError(e));
    }
    setBusy("");
  };

  const sendMagic = async () => {
    setBusy("magic");
    try {
      const res = await api.post("/donor/magic-link", { email: form.email });
      setMagicSent(res.data);
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!cfg) {
    return <Shell><div className="flex items-center gap-2 text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading…</div></Shell>;
  }

  return (
    <Shell>
      <div className="panel w-full max-w-sm p-7" data-testid="donor-signin-card">
        <div className="flex items-center gap-2.5">
          {cfg.logo_url
            ? <img src={cfg.logo_url} alt="" className="h-9 w-9 rounded-[10px] object-cover" />
            : <span className="grid h-9 w-9 place-items-center rounded-[10px] bg-gradient-to-br from-primary to-[hsl(var(--teal))]">
                <Heart className="h-4 w-4 text-white" />
              </span>}
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[1.6px] text-muted-foreground">Donor account</div>
            <div className="truncate font-display text-[17px] font-bold text-foreground"
                 data-testid="donor-signin-org">{cfg.organisation}</div>
          </div>
        </div>

        {token && (
          <div className="mt-5 flex items-center gap-2 text-[12.5px] text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Opening your account…
          </div>
        )}

        {!token && mode === "password" && (
          <div className="mt-5 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[11px] text-muted-foreground">Email address</Label>
              <Input value={form.email} data-testid="donor-email-input" autoComplete="username"
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     className="bg-muted/40 font-mono text-[12.5px]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] text-muted-foreground">Password</Label>
              <Input type="password" value={form.password} data-testid="donor-password-input"
                     autoComplete="current-password"
                     onKeyDown={(e) => e.key === "Enter" && signIn()}
                     onChange={(e) => setForm({ ...form, password: e.target.value })}
                     className="bg-muted/40" />
            </div>
            <Button onClick={signIn} data-testid="donor-signin-button"
                    disabled={busy === "in" || !form.email || !form.password}
                    className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "in" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <LockKeyhole className="mr-2 h-4 w-4" />} Sign in
            </Button>
            <button type="button" onClick={() => setMode("magic")} data-testid="donor-magic-switch"
                    className="w-full text-center text-[11.5px] text-primary transition-opacity duration-150 hover:opacity-75">
              Email me a sign-in link instead
            </button>
            {cfg.signup_mode !== "invite_only" && (
              <button type="button" onClick={() => setMode("register")}
                      data-testid="donor-register-switch"
                      className="w-full text-center text-[11.5px] text-primary transition-opacity duration-150 hover:opacity-75">
                First time here? Create my account
              </button>
            )}
          </div>
        )}

        {!token && mode === "magic" && (
          <div className="mt-5 space-y-3">
            {!magicSent && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-muted-foreground">Email address</Label>
                  <Input value={form.email} data-testid="donor-magic-email-input"
                         onChange={(e) => setForm({ ...form, email: e.target.value })}
                         className="bg-muted/40 font-mono text-[12.5px]" />
                </div>
                <Button onClick={sendMagic} data-testid="donor-magic-button"
                        disabled={busy === "magic" || !form.email}
                        className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {busy === "magic" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <Mail className="mr-2 h-4 w-4" />} Send my sign-in link
                </Button>
              </>
            )}
            {magicSent && (
              <div className="space-y-2 rounded-xl border border-border bg-muted/25 p-3"
                   data-testid="donor-magic-sent">
                <p className="text-[12.5px] text-foreground">{magicSent.message}</p>
                {magicSent.link && (
                  <>
                    <p className="text-[11px] text-muted-foreground">
                      Email delivery is not connected for this charity yet, so here is your link:
                    </p>
                    <a href={magicSent.link} data-testid="donor-magic-inline-link"
                       className="block break-all font-mono text-[11px] text-primary hover:underline">
                      {magicSent.link}
                    </a>
                  </>
                )}
              </div>
            )}
            <button type="button" onClick={() => { setMode("password"); setMagicSent(null); }}
                    data-testid="donor-password-switch"
                    className="w-full text-center text-[11.5px] text-primary transition-opacity duration-150 hover:opacity-75">
              Use my password instead
            </button>
          </div>
        )}

        {!token && mode === "register" && (
          <div className="mt-5 space-y-3" data-testid="donor-register-panel">
            {!registered ? (
              <>
                <p className="text-[11.5px] text-muted-foreground">
                  {cfg.signup_note || "Use the email address you gave with and we will match it to "
                    + "your giving record."}
                  {cfg.signup_mode === "approval"
                    ? " Our fundraising team approves new accounts before they open."
                    : ""}
                </p>
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-muted-foreground">Email address</Label>
                  <Input value={form.email} data-testid="donor-register-email"
                         onChange={(e) => setForm({ ...form, email: e.target.value })}
                         className="bg-muted/40 font-mono text-[12.5px]" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-muted-foreground">Your name</Label>
                  <Input value={form.name || ""} data-testid="donor-register-name"
                         onChange={(e) => setForm({ ...form, name: e.target.value })}
                         className="bg-muted/40 text-[12.5px]" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] text-muted-foreground">
                    Choose a password (10 characters or more)
                  </Label>
                  <Input type="password" value={form.password} data-testid="donor-register-password"
                         autoComplete="new-password"
                         onChange={(e) => setForm({ ...form, password: e.target.value })}
                         className="bg-muted/40" />
                </div>
                <Button onClick={register} data-testid="donor-register-button"
                        disabled={busy === "reg" || !form.email || form.password.length < 10}
                        className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {busy === "reg" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <UserPlus className="mr-2 h-4 w-4" />} Create my account
                </Button>
              </>
            ) : (
              <div className="rounded-xl border border-border bg-muted/25 p-3"
                   data-testid="donor-register-done">
                <p className="text-[12.5px] text-foreground">{registered}</p>
              </div>
            )}
            <button type="button" onClick={() => { setMode("password"); setRegistered(""); }}
                    data-testid="donor-register-back"
                    className="w-full text-center text-[11.5px] text-primary transition-opacity duration-150 hover:opacity-75">
              Back to sign in
            </button>
          </div>
        )}

        <p className="mt-5 flex items-center justify-center gap-1.5 text-[10.5px] text-muted-foreground">
          <ShieldCheck className="h-3 w-3" /> This is {cfg.organisation}'s own donor account — your
          details are never shared.
        </p>
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="grid min-h-screen place-items-center bg-background px-4 py-10">{children}</div>
);
